import type { FC, SVGProps } from "react"

import type { RecoilState } from "recoil"
import { useRecoilState } from "recoil"
import type { FlattenSimpleInterpolation } from "styled-components"

import { Wrapper } from "../../utils/Wrapper"

export type RadioIconProps = {
  active?: SVGProps<SVGSVGElement>
  inactive?: SVGProps<SVGSVGElement>
  disabled?: SVGProps<SVGSVGElement>
}

export type RadioProps<T extends number | string> = {
  label?: string
  state: RecoilState<T | null> | RecoilState<T>
  options: ReadonlyArray<T>
  getName?: (option: T) => string
  getSummary?: (option: T) => string
  shouldDisable?: (option: T) => boolean
  shouldHide?: (option: T) => boolean
  icon?: {
    get: (option: T) => FC<SVGProps<SVGSVGElement>>
    props?: RadioIconProps | ((option: T) => RadioIconProps)
  }
  css?: FlattenSimpleInterpolation
}

export const Radio = <T extends string>({
  label,
  options,
  state,
  css,
  icon,
  getName = (v) => v,
  shouldDisable,
}: RadioProps<T>): ReturnType<FC<RadioProps<T>>> => {
  const [value, setValue] = useRecoilState(state as RecoilState<T | null>)

  return (
    <Wrapper css={css}>
      {label && <label data-role="main">{label}</label>}
      {options.map((v) => {
        const isDisabled = shouldDisable?.(v) ?? false

        return (
          <label key={v} data-role="option">
            <input
              data-testid={`radio-option-${v}`}
              type="radio"
              value={v ?? ``}
              checked={v === value}
              onChange={() => setValue(v)}
              disabled={isDisabled}
            />
            <div data-role="panel">
              {(() => {
                if (icon) {
                  const Icon = icon.get(v)
                  const propsObject =
                    typeof icon.props === `function` ? icon.props(v) : icon.props
                  const props =
                    v === value
                      ? propsObject?.active
                      : isDisabled
                        ? propsObject?.disabled
                        : propsObject?.inactive

                  return <Icon {...props} />
                }
              })()}
              <span>{getName(v)}</span>
            </div>
          </label>
        )
      })}
    </Wrapper>
  )
}
